<template>
  <div class="min-h-screen">
    <Header />
    
    <!-- Hero Section -->
    <section class="page-hero-shell pt-32 pb-20">
      <div class="absolute inset-0 page-overlay-layer space-pattern opacity-50"></div>
      <div class="hero-orb page-overlay-layer hero-orb-a"></div>
      <div class="hero-orb page-overlay-layer hero-orb-b"></div>
      <div class="hero-orb page-overlay-layer hero-orb-c"></div>
      
      <div class="page-content-layer max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div class="text-center mb-16">
          <div class="hero-badge mb-6">
            <span>产品矩阵</span>
          </div>
          <h1 class="text-5xl md:text-7xl font-bold mb-6">
            <span class="gradient-text">产品生态体系</span>
          </h1>
          <p class="text-xl text-gray-700 dark:text-dark-muted max-w-3xl mx-auto">
            六大核心产品构建完整数字营销生态，覆盖从数据管理到投放转化的全链路，助力企业实现数字化增长
          </p>
        </div>
      </div>
    </section>

    <!-- Products Grid -->
    <section class="py-20">
      <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div class="grid md:grid-cols-2 lg:grid-cols-3 gap-8 p-8 md:p-10">
          <div v-for="product in products" :key="product.id"
               class="group">
            <div class="card relative h-full border border-primary-20 dark:border-white/10 visual-surface overflow-hidden">
              <div class="card-corner-accent" aria-hidden="true"></div>

              <div class="relative z-10">
                <div class="w-16 h-16 icon-shell icon-shell-strong mb-6 group-hover:scale-110 transition-transform duration-500">
                  <FeatureIcon :name="product.iconName || 'targeting'" size="xl" variant="minimal" />
                </div>

                <h3 class="text-2xl font-bold mb-3 text-gray-900 dark:text-white">{{ product.title }}</h3>
                <p class="text-cosmic-adaptive font-medium mb-4">{{ product.subtitle }}</p>
                <p class="text-gray-700 dark:text-dark-muted text-sm mb-6">{{ product.description }}</p>

                <div class="space-y-2 mb-6">
                  <div v-for="feature in product.features" :key="feature"
                       class="flex items-center text-sm text-gray-700 dark:text-dark-muted">
                    <svg class="w-4 h-4 mr-2 text-cosmic" fill="currentColor" viewBox="0 0 20 20">
                      <path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd"></path>
                    </svg>
                    {{ feature }}
                  </div>
                </div>

                <NuxtLink :to="`/products/${product.slug}`" class="flex items-center text-cosmic-adaptive group-hover:text-primary-700 dark:group-hover:text-white transition-colors">
                  了解详情
                  <svg class="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7"></path>
                  </svg>
                </NuxtLink>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- CTA Section -->
    <section class="py-32 relative">
      <div class="absolute inset-0 page-overlay-layer bg-gradient-to-r from-cosmic/10 via-nebula/10 to-stellar/10"></div>
      <div class="hero-orb page-overlay-layer hero-orb-a opacity-60"></div>
      
      <div class="page-content-layer max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
        <h2 class="text-4xl md:text-5xl font-bold mb-6 gradient-text">找到适合您的解决方案</h2>
        <p class="text-xl text-gray-700 dark:text-dark-muted mb-12">
          我们的产品团队随时准备为您提供专业的咨询和定制化解决方案
        </p>
        
        <div class="flex flex-col sm:flex-row gap-6 justify-center">
          <button @click="scrollToAboutContact" class="btn-primary">预约演示</button>
          <button @click="scrollToAboutContact" class="btn-secondary">联系我们</button>
        </div>
      </div>
    </section>

    <Footer />
  </div>
</template>

<script setup>
const products = [
  {
    id: 1,
    title: 'MediaGet SSP',
    subtitle: '媒体管理平台',
    description: '智能化的媒体资源管理系统，扩大媒体方收益',
    icon: '📺',
    iconName: 'ssp',
    slug: 'ssp',
    features: ['实时竞价', '收益优化', '多渠道管理']
  },
  {
    id: 2,
    title: 'Adget DSP',
    subtitle: '广告管理平台',
    description: '精准的广告投放系统，实现跨媒体的智能投放',
    icon: '🎯',
    iconName: 'dsp',
    slug: 'dsp',
    features: ['精准定向', '智能投放', '效果追踪']
  },
  {
    id: 3,
    title: 'MagicAd SDK',
    subtitle: '移动广告SDK',
    description: '安全合规的移动广告变现SDK，通过多项权威认证',
    icon: '📱',
    iconName: 'sdk',
    slug: 'sdk',
    features: ['安全认证', '双端支持', '高收益']
  },
  {
    id: 4,
    title: '海外ADX',
    subtitle: '海外广告交易平台',
    description: '连接全球广告资源，拓展国际市场',
    icon: '🌍',
    iconName: 'global',
    slug: 'adx',
    features: ['全球覆盖', '跨境投放', '本地化运营']
  },
  {
    id: 5,
    title: '开户投流',
    subtitle: '一站式开户服务',
    description: '快速开通各类广告账户，启动专业投放服务',
    icon: '⚡',
    iconName: 'speed',
    slug: 'account',
    features: ['快速开户', '专业代运营', '效果保障']
  },
  {
    id: 6,
    title: '私域流量',
    subtitle: '私域运营解决方案',
    description: '构建企业私域流量池，提升客户终身价值',
    icon: '💎',
    iconName: 'private-domain',
    slug: 'private-domain',
    features: ['用户沉淀', '精准营销', '价值挖掘']
  }
]

const scrollToAboutContact = () => {
  window.location.href = '/about#contact'
}

</script>
